import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  Drawer, 
  Typography, 
  Tag, 
  List, 
  Avatar, 
  Space, 
  Button, 
  Table, 
  Input, 
  Select,
  Card,
  Row,
  Col,
  Tooltip,
  message
} from 'antd';
import { SearchOutlined, DownloadOutlined, CopyOutlined, LinkOutlined } from '@ant-design/icons';
import { groupsAPI, accountsAPI } from '../services/api';
import { pickBestSyncedGroupRecord } from '../utils/groupDisplay';
import './Groups.css';

const { Title, Text } = Typography;
const { Search } = Input;

// Custom hook for debounced value
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

interface Account {
  _id: string;
  phoneNumber: string;
  accountName: string;
  status: string;
  qrCode?: string | null;
  lastSync?: string | null;
  groupsCount?: number;
  membersCount?: number;
}

interface Group {
  _id: string;
  groupId?: string;
  name: string;
  description: string;
  inviteLink?: string;
  participantsCount: number;
  accountId: {
    _id: string;
    accountName: string;
    phoneNumber: string;
    status?: string;
  };
  participants?: Array<{
    id: string;
    name: string;
    phoneNumber: string;
    isAdmin: boolean;
    isSuperAdmin: boolean;
  }>;
}

/** Same group (same groupId) connected via multiple accounts – one row per group */
interface GroupedGroupRow {
  __grouped: true;
  groupKey: string;
  name: string;
  description: string;
  participantsCount: number;
  inviteLink?: string;
  accounts: Array<{ _id: string; accountName: string }>;
  records: Group[];
}

const Groups: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedAccount, setSelectedAccount] = useState<string>('all');
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [activeTab, setActiveTab] = useState<'active' | 'archived'>('active');
  
  // Filters and pagination
  const [searchText, setSearchText] = useState<string>('');
  const debouncedSearchText = useDebounce(searchText, 500); // 500ms debounce
  const [memberFilter, setMemberFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [memberSearchText, setMemberSearchText] = useState<string>('');
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [loadingGroupDetails, setLoadingGroupDetails] = useState<boolean>(false);

  // Memoized query keys
  const accountsQueryKey = useMemo(() => ['accounts'], []);
  const groupsQueryKey = useMemo(() => ['groups', 'active', selectedAccount], [selectedAccount]);
  const archivedGroupsQueryKey = useMemo(() => ['groups', 'archived'], []);

  // Memoized query functions
  const fetchAccounts = useCallback(async () => {
    const response = await accountsAPI.getAll();
    return response.data;
  }, []);

  const fetchActiveGroups = useCallback(async () => {
    let response;
    if (selectedAccount === 'all') {
      response = await groupsAPI.getAll();
    } else {
      response = await groupsAPI.getByAccount(selectedAccount);
    }
    return response.data;
  }, [selectedAccount]);

  const fetchArchivedGroups = useCallback(async () => {
    const response = await groupsAPI.getArchived();
    return response.data;
  }, []);

  // Fetch accounts - optimized caching
  const { data: accountsData } = useQuery({
    queryKey: accountsQueryKey,
    queryFn: fetchAccounts,
    staleTime: 15 * 60 * 1000, // 15 minutes - data stays fresh longer
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache longer
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch if data exists in cache
  });
  
  // Memoized accounts array
  const accounts = useMemo(() => accountsData || [], [accountsData]);

  // Fetch active groups - optimized caching
  const { 
    data: groupsData, 
    isLoading: loading, 
    isFetching: isFetchingActive,
    error: groupsError 
  } = useQuery({
    queryKey: groupsQueryKey,
    queryFn: fetchActiveGroups,
    enabled: activeTab === 'active',
    staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh longer
    gcTime: 15 * 60 * 1000, // 15 minutes - keep in cache longer
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch if data exists in cache
  });

  // Fetch archived groups - optimized caching
  const { 
    data: archivedGroupsData, 
    isLoading: archivedLoading, 
    isFetching: isFetchingArchived,
    error: archivedError 
  } = useQuery({
    queryKey: archivedGroupsQueryKey,
    queryFn: fetchArchivedGroups,
    enabled: activeTab === 'archived',
    staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh longer
    gcTime: 15 * 60 * 1000, // 15 minutes - keep in cache longer
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch if data exists in cache
  });

  // Memoized computed values
  const groups = useMemo(() => groupsData || [], [groupsData]);
  const archivedGroups = useMemo(() => archivedGroupsData || [], [archivedGroupsData]);
  const isLoading = useMemo(() => activeTab === 'active' ? loading : archivedLoading, [activeTab, loading, archivedLoading]);
  const isFetching = useMemo(() => activeTab === 'active' ? isFetchingActive : isFetchingArchived, [activeTab, isFetchingActive, isFetchingArchived]);
  const error = useMemo(() => 
    activeTab === 'active' 
      ? (groupsError as any)?.response?.data?.error || (groupsError ? 'Failed to fetch groups' : null)
      : (archivedError as any)?.response?.data?.error || (archivedError ? 'Failed to fetch archived groups' : null),
    [activeTab, groupsError, archivedError]
  );



  // Filter groups based on search and filters - use debounced search
  const filteredGroups = useMemo(() => {
    const sourceGroups = activeTab === 'active' ? groups : archivedGroups;
    let filtered = [...sourceGroups];

    // Search filter - use debounced search term
    if (debouncedSearchText) {
      const searchLower = debouncedSearchText.toLowerCase();
      filtered = filtered.filter(group => 
        group.name?.toLowerCase().includes(searchLower) ||
        group.description?.toLowerCase().includes(searchLower) ||
        group.accountId?.accountName?.toLowerCase().includes(searchLower)
      );
    }

    // Member count filter
    if (memberFilter !== 'all') {
      const [min, max] = memberFilter.split('-').map(Number);
      if (max) {
        filtered = filtered.filter(group => 
          group.participantsCount >= min && group.participantsCount <= max
        );
      } else {
        filtered = filtered.filter(group => group.participantsCount >= min);
      }
    }

    return filtered;
  }, [groups, archivedGroups, debouncedSearchText, memberFilter, activeTab]);

  // When "All Accounts" is selected, group by same group (groupId) so one row per unique group with all linked accounts
  const groupedBySameGroup = useMemo((): GroupedGroupRow[] => {
    if (selectedAccount !== 'all' || activeTab !== 'active' || filteredGroups.length === 0) return [];
    const byKey = new Map<string, Group[]>();
    for (const g of filteredGroups) {
      const key = (g as Group).groupId || g._id;
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key)!.push(g);
    }
    return Array.from(byKey.entries()).map(([groupKey, records]) => {
      const best = pickBestSyncedGroupRecord(records);
      const accounts = records.map(r => ({
        _id: r.accountId?._id ?? '',
        accountName: r.accountId?.accountName ?? 'Unknown'
      }));
      return {
        __grouped: true as const,
        groupKey,
        name: best.name ?? 'Unnamed Group',
        description: best.description ?? '',
        participantsCount: best.participantsCount ?? 0,
        inviteLink: best.inviteLink,
        accounts,
        records
      };
    });
  }, [filteredGroups, selectedAccount, activeTab]);

  // Display list: grouped rows when "All Accounts", else flat list
  const displayList = useMemo((): (Group | GroupedGroupRow)[] => {
    if (selectedAccount === 'all' && activeTab === 'active' && groupedBySameGroup.length > 0) {
      return groupedBySameGroup;
    }
    return filteredGroups;
  }, [selectedAccount, activeTab, filteredGroups, groupedBySameGroup]);

  // Pagination over display list
  const paginatedGroups = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return displayList.slice(start, end);
  }, [displayList, currentPage, pageSize]);

  // Debounced member search in drawer
  const debouncedMemberSearchText = useDebounce(memberSearchText, 300);

  // Memoize selectedGroup participants separately for better performance
  const selectedGroupParticipants = useMemo(() => {
    return selectedGroup?.participants || [];
  }, [selectedGroup?.participants]);

  // Deduplicate participants by phoneNumber or id (same person can appear multiple times)
  const uniqueGroupParticipants = useMemo(() => {
    if (!selectedGroupParticipants.length) return [];
    const seen = new Set<string>();
    return selectedGroupParticipants.filter((member: any) => {
      const key = member.phoneNumber || member.id || '';
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [selectedGroupParticipants]);

  // Filter members in drawer - optimized with memoized unique participants
  const filteredMembers = useMemo(() => {
    if (!selectedGroup || uniqueGroupParticipants.length === 0) return [];
    if (!debouncedMemberSearchText) return uniqueGroupParticipants;
    
    const searchLower = debouncedMemberSearchText.toLowerCase();
    return uniqueGroupParticipants.filter((member: any) =>
      member.name?.toLowerCase().includes(searchLower) ||
      member.phoneNumber?.includes(searchLower)
    );
  }, [selectedGroup, uniqueGroupParticipants, debouncedMemberSearchText]);

  // Memoized download handlers - client-side CSV generation for faster downloads
  const downloadGroupNumbers = useCallback(() => {
    if (!selectedGroup || filteredMembers.length === 0) {
      message.warning({ content: 'No members to download', key: 'groupDownload' });
      return;
    }
    
    try {
      message.loading({ content: 'Generating CSV...', key: 'groupDownload', duration: 0 });
      
      // Generate CSV content directly from filtered members (already available on client)
      const csvHeaders = 'Name,Phone Number,Role,Invite Link\n';
      const inviteLink = (selectedGroup.inviteLink || '').replace(/"/g, '""');
      
      // Convert filtered members to CSV rows
      const csvRows = filteredMembers.map(member => {
        const name = (member.name || '').replace(/"/g, '""');
        const phoneNumber = member.phoneNumber || member.id || '';
        let role = 'Member';
        if (member.isSuperAdmin) role = 'Super Admin';
        else if (member.isAdmin) role = 'Admin';
        
        return `"${name}",${phoneNumber},"${role}","${inviteLink}"`;
      });
      
      // Combine headers and rows
      const csvContent = csvHeaders + csvRows.join('\n');
      
      // Create CSV blob with BOM for Excel compatibility
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Download file
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      const safeGroupName = (selectedGroup.name || 'group').replace(/[^a-z0-9]/gi, '_').toLowerCase();
      link.setAttribute('download', `${safeGroupName}_participants_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      message.success({ 
        content: `CSV downloaded successfully (${filteredMembers.length} members)`, 
        key: 'groupDownload' 
      });
    } catch (err: any) {
      message.error({ content: 'Failed to generate CSV', key: 'groupDownload' });
      console.error('CSV generation error:', err);
    }
  }, [selectedGroup, filteredMembers]);

  const downloadAllGroups = useCallback(async () => {
    if (isDownloading) return;
    
    try {
      setIsDownloading(true);
      message.loading({ content: 'Preparing CSV download...', key: 'allGroupsDownload', duration: 0 });
      
      // Fetch groups data
      let groupsData: Group[] = [];
      
      if (activeTab === 'archived') {
        const response = await groupsAPI.getArchived();
        groupsData = response.data || [];
      } else {
        if (selectedAccount === 'all') {
          const response = await groupsAPI.getAll({ 
            search: debouncedSearchText || undefined
          });
          groupsData = response.data || [];
        } else {
          const response = await groupsAPI.getByAccount(selectedAccount);
          groupsData = response.data || [];
        }
      }
      
      // Apply search filter if needed (since backend might not support it for archived)
      if (debouncedSearchText && activeTab === 'archived') {
        const searchLower = debouncedSearchText.toLowerCase();
        groupsData = groupsData.filter(group => 
          group.name?.toLowerCase().includes(searchLower) ||
          group.description?.toLowerCase().includes(searchLower) ||
          group.accountId?.accountName?.toLowerCase().includes(searchLower)
        );
      }
      
      // Prepare CSV content
      const csvHeaders = ['Group Name', 'Account Name', 'Phone Number', 'Members Count', 'Description', 'Invite Link'];
      const csvRows = groupsData.map(group => {
        const groupName = (group.name || '').replace(/"/g, '""');
        const accountName = (group.accountId?.accountName || 'Unknown').replace(/"/g, '""');
        const phoneNumber = (group.accountId?.phoneNumber || '').replace(/"/g, '""');
        const membersCount = group.participantsCount || (group.participants?.length || 0);
        const description = (group.description || '').replace(/"/g, '""');
        const inviteLink = (group.inviteLink || '').replace(/"/g, '""');
        
        return `"${groupName}","${accountName}","${phoneNumber}",${membersCount},"${description}","${inviteLink}"`;
      });
      
      // Combine headers and rows
      const csvContent = [csvHeaders.join(','), ...csvRows].join('\n');
      
      // Create CSV blob with BOM for Excel compatibility
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Download file
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `groups_${activeTab}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      message.success({ content: 'CSV file downloaded successfully', key: 'allGroupsDownload' });
    } catch (err: any) {
      message.error({ content: err.response?.data?.error || 'Failed to download Excel file', key: 'allGroupsDownload' });
    } finally {
      setIsDownloading(false);
    }
  }, [activeTab, debouncedSearchText, selectedAccount, isDownloading]);

  // Memoized filter handlers
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleSearch = useCallback((value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  }, []);

  const handleSearchEnter = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    setSearchText(target.value);
    setCurrentPage(1);
  }, []);

  const handleAccountChange = useCallback((value: string) => {
    setSelectedAccount(value);
    setCurrentPage(1);
  }, []);

  const handleMemberFilterChange = useCallback((value: string) => {
    setMemberFilter(value);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number, size?: number) => {
    setCurrentPage(page);
    if (size) {
      setPageSize(size);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleActiveTabClick = useCallback(() => {
    setActiveTab('active');
    setSelectedAccount('all');
    setCurrentPage(1);
  }, []);

  const handleArchivedTabClick = useCallback(() => {
    setActiveTab('archived');
    setSelectedAccount('all');
    setCurrentPage(1);
  }, []);

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: activeTab === 'active' ? groupsQueryKey : archivedGroupsQueryKey });
  }, [queryClient, activeTab, groupsQueryKey, archivedGroupsQueryKey]);

  const handleDrawerClose = useCallback(() => {
    setSelectedGroup(null);
    setMemberSearchText('');
  }, []);

  const handleViewDetails = useCallback(async (record: Group) => {
    // If the group already has participants, use it directly
    if (record.participants && Array.isArray(record.participants) && record.participants.length > 0) {
      setSelectedGroup(record);
      return;
    }
    
    // Otherwise, fetch full group details with participants
    setLoadingGroupDetails(true);
    try {
      const response = await groupsAPI.getById(record._id);
      setSelectedGroup(response.data);
    } catch (error: any) {
      console.error('Failed to fetch group details:', error);
      message.error('Failed to load group details. Showing available information.');
      // Fallback to the record we have even without participants
      setSelectedGroup(record);
    } finally {
      setLoadingGroupDetails(false);
    }
  }, []);

  // Table columns - memoized (support both Group and GroupedGroupRow)
  const columns = useMemo(() => [
    {
      title: 'Group Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: Group | GroupedGroupRow, b: Group | GroupedGroupRow) => (a.name || '').localeCompare(b.name || ''),
      render: (text: string) => <Text strong>{text || 'Unnamed Group'}</Text>,
    },
    {
      title: 'Account(s)',
      key: 'account',
      sorter: (a: Group | GroupedGroupRow, b: Group | GroupedGroupRow) => {
        const aStr = 'accounts' in a && a.accounts ? a.accounts.map((ac: { accountName: string }) => ac.accountName).join(', ') : (a as Group).accountId?.accountName || '';
        const bStr = 'accounts' in b && b.accounts ? b.accounts.map((ac: { accountName: string }) => ac.accountName).join(', ') : (b as Group).accountId?.accountName || '';
        return aStr.localeCompare(bStr);
      },
      render: (_: unknown, record: Group | GroupedGroupRow) => {
        if ('__grouped' in record && record.__grouped && record.accounts?.length) {
          return (
            <Space size={[4, 4]} wrap>
              {record.accounts.map((ac) => (
                <Tag key={ac._id} color="blue">{ac.accountName}</Tag>
              ))}
            </Space>
          );
        }
        return <Text>{(record as Group).accountId?.accountName ?? 'N/A'}</Text>;
      },
    },
    {
      title: 'Members',
      dataIndex: 'participantsCount',
      key: 'members',
      sorter: (a: Group | GroupedGroupRow, b: Group | GroupedGroupRow) => a.participantsCount - b.participantsCount,
      render: (count: number) => (
        <Tag color="#0e4842">{count}</Tag>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: (text: string) => {
        const description = text || 'No description';
        return (
          <Tooltip placement="topLeft" title={description}>
            <div style={{ 
              maxWidth: '200px', 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap' 
            }}>
              {description}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Invite Link',
      dataIndex: 'inviteLink',
      key: 'inviteLink',
      width: 250,
      render: (inviteLink: string, record: Group | GroupedGroupRow) => {
        if (!inviteLink) {
          return <Text type="secondary">N/A</Text>;
        }
        return (
          <Space size="small">
            <Tooltip title={inviteLink}>
              <Text 
                ellipsis 
                style={{ maxWidth: '180px', display: 'inline-block' }}
              >
                {inviteLink}
              </Text>
            </Tooltip>
            <Button
              type="link"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => {
                navigator.clipboard.writeText(inviteLink);
                message.success('Invite link copied!');
              }}
              style={{ padding: 0, minWidth: 'auto' }}
            />
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Group | GroupedGroupRow) => {
        const groupRecord: Group = 'records' in record && record.records?.length ? pickBestSyncedGroupRecord(record.records) : (record as Group);
        return (
          <Button
            type="primary"
            onClick={() => handleViewDetails(groupRecord)}
          >
            View Details
          </Button>
        );
      },
    },
  ], [handleViewDetails]);

  // Memoized account options
  const accountOptions = useMemo(() => [
    { label: 'All Accounts', value: 'all' },
    ...accounts.map((account: Account) => ({
      label: `${account.accountName} (${account.status})`,
      value: account._id
    }))
  ], [accounts]);

  // Memoized pagination config (total = display list length for grouped or flat)
  const paginationConfig = useMemo(() => ({
    current: currentPage,
    pageSize: pageSize,
    total: displayList.length,
    showSizeChanger: true,
    showTotal: (total: number, range: [number, number]) => 
      `${range[0]}-${range[1]} of ${total} groups`,
    pageSizeOptions: ['10', '20', '50', '100'],
    onChange: handlePageChange,
  }), [currentPage, pageSize, displayList.length, handlePageChange]);

  return (
    <div className="groups-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <Title level={2} style={{ margin: 0 }}>WhatsApp Groups</Title>
        <Space>
          <Button 
            onClick={handleRefresh}
            loading={isFetching}
          >
            Refresh
          </Button>
          <Button 
            type="primary"
            icon={<DownloadOutlined />}
            onClick={downloadAllGroups}
            loading={isDownloading}
            disabled={isDownloading}
          >
            {isDownloading ? 'Downloading...' : 'Download All Groups (CSV)'}
          </Button>
        </Space>
      </div>

      {error && <div className="error">{error}</div>}

      {/* Tabs */}
      <div style={{ marginBottom: '1rem' }}>
        <Button 
          type={activeTab === 'active' ? 'primary' : 'default'}
          onClick={handleActiveTabClick}
          style={{ marginRight: '0.5rem' }}
        >
          Active Groups
        </Button>
        <Button 
          type={activeTab === 'archived' ? 'primary' : 'default'}
          onClick={handleArchivedTabClick}
        >
          Archived Groups
        </Button>
      </div>

      <Card>
        {/* Filters Section */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="Search groups by name, description, or account"
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              value={searchText}
              onChange={handleSearchChange}
              onSearch={handleSearch}
              onPressEnter={handleSearchEnter}
            />
          </Col>
          {activeTab === 'active' && selectedAccount === 'all' && displayList.length > 0 && (
            <Col xs={24}>
              <Text type="secondary">
                Groups connected with multiple accounts are shown once; account(s) are listed in the Account(s) column.
              </Text>
            </Col>
          )}
          {activeTab === 'active' && (
            <Col xs={24} sm={12} md={8}>
              <Select
                placeholder="Filter by Account"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                size="large"
                style={{ width: '100%' }}
                value={selectedAccount}
                onChange={handleAccountChange}
                options={accountOptions}
              />
            </Col>
          )}
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder="Filter by Member Count"
              showSearch={false}
              size="large"
              style={{ width: '100%' }}
              value={memberFilter}
              onChange={handleMemberFilterChange}
              options={[
                { label: 'All Sizes', value: 'all' },
                { label: '1-10 members', value: '1-10' },
                { label: '11-50 members', value: '11-50' },
                { label: '51-100 members', value: '51-100' },
                { label: '101-200 members', value: '101-200' },
                { label: '201-500 members', value: '201-500' },
                { label: '500+ members', value: '500' },
              ]}
            />
          </Col>
        </Row>

        {/* Table */}
        {(activeTab === 'active' ? groups.length === 0 : archivedGroups.length === 0) ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Text type="secondary">
              {activeTab === 'active' 
                ? 'No groups found. Connect an account and sync to see groups.'
                : 'No archived groups found.'}
            </Text>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={paginatedGroups}
            rowKey={(record) => ('__grouped' in record && record.__grouped ? record.groupKey : (record as Group)._id)}
            loading={isLoading}
            pagination={paginationConfig}
          />
        )}
      </Card>

      <Drawer
        title={selectedGroup?.name}
        placement="right"
        onClose={handleDrawerClose}
        open={!!selectedGroup}
        width={600}
      >
        {loadingGroupDetails ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Text>Loading group details...</Text>
          </div>
        ) : selectedGroup ? (
          <div className="flex flex-col space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <Space direction="vertical" size="small" className="w-full">
                <div>
                  <Text strong>Account: </Text>
                  <Text>{selectedGroup.accountId?.accountName || 'N/A'}</Text>
                </div>
                <div>
                  <Text strong>Members: </Text>
                  <Text>{selectedGroup.participantsCount}</Text>
                </div>
                {selectedGroup.description && (
                  <div>
                    <Text strong>Description: </Text>
                    <Text>{selectedGroup.description}</Text>
                  </div>
                )}
                {selectedGroup.inviteLink && (
                  <div>
                    <Text strong>Invite Link: </Text>
                    <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Input
                        value={selectedGroup.inviteLink}
                        readOnly
                        prefix={<LinkOutlined />}
                        style={{ flex: 1, minWidth: 200 }}
                      />
                      <Button
                        type="primary"
                        icon={<CopyOutlined />}
                        onClick={() => {
                          navigator.clipboard.writeText(selectedGroup.inviteLink || '');
                          message.success('Invite link copied to clipboard!');
                        }}
                      >
                        Copy
                      </Button>
                    </div>
                  </div>
                )}
              </Space>
            </div>
            
            <div>
              <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <Title level={5} style={{ margin: 0 }}>
                  Members ({filteredMembers.length} of {uniqueGroupParticipants.length})
                </Title>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={downloadGroupNumbers}
                    size="small"
                  >
                    Download Numbers (CSV)
                  </Button>
                  <Search
                    placeholder="Search members"
                    allowClear
                    style={{ width: 250 }}
                    value={memberSearchText}
                    onChange={(e) => setMemberSearchText(e.target.value)}
                  />
                </div>
              </div>
              <List
                dataSource={filteredMembers}
                renderItem={(member, index) => (
                  <List.Item
                    key={index}
                    className="flex justify-between items-center"
                  >
                    <List.Item.Meta
                      avatar={<Avatar>{(member.name || member.phoneNumber || '?').charAt(0).toUpperCase()}</Avatar>}
                      title={member.name || member.phoneNumber || 'Unknown'}
                      description={member.phoneNumber}
                    />
                    <Space>
                      {member.isSuperAdmin && (
                        <Tag color="red">Super Admin</Tag>
                      )}
                      {member.isAdmin && !member.isSuperAdmin && (
                        <Tag color="#0e4842">Admin</Tag>
                      )}
                    </Space>
                  </List.Item>
                )}
                locale={{ emptyText: 'No members found' }}
              />
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Text type="secondary">No group selected</Text>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default Groups;

